import updateQuoteApi from '@/api/patch/company/sales-flow/update-quote';
import Alert from '@/components/alert/alert-component';
import OutletWrapper from '@/components/animations/animated-routes/animated-outlet';
import BlackBottomBar from '@/components/black-bottom-bar/black-bottom-bar';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import Card from '@/components/card/card-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { Col, Row } from '@/components/grid/grid-component';
import ImageCarousel from '@/components/image-carousel/image-carousel-component';
import Loader from '@/components/loader/loader-component';
import TabSelector from '@/components/page-tabs/page-tab-selector-component';
import { useMutation } from '@/hooks/use-api';
import { APP_CONTEXT } from '@/utils/context';
import { Heading2Text } from '@/utils/global-fonts';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import { useContext, useEffect, useState, useCallback } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import {
  useLocation,
  useMatch,
  useNavigate,
  useRouteLoaderData,
  useParams,
} from 'react-router-dom';
import styled from 'styled-components';
import getSupplierUEImageApi from '@/api/post/dealer/get-ue-images';
import getQuotePdfApi from '../../api/get/dealer/get-quote-pdf';
import { SummaryContextProvider } from './context-provider';
import recursiveGetOptionName from '../sales-flow/components/configuration/utils';

const TabHolder = styled.div`
  padding: 32px;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled.div`
  margin-top: 20px;
`;

/**
 * Page that shows the Quote Summary
 * @returns {JSX.Element} The rendered Quote Summary page component.
 */
const MainSummary = () => {
  const { state } = useLocation();
  const { id: qId } = useParams();
  const navigate = useNavigate();
  const {
    formData = null,
    quoteId = qId,
    ueImages: stateUeImages,
    quoteManagement,
  } = state || {};
  const [ueImages, setUeImages] = useState(stateUeImages ?? []);
  const [ueImage] = useMutation(getSupplierUEImageApi);
  const [summary, setSummary] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [quoteCustomer, setQuoteCustomer] = useState(null);
  const [formsLoading, setFormsLoading] = useState(false);
  const typeEnquire = !!useMatch(`shop/sales/main-summary/${quoteId}/enquire`);
  const routerData = useRouteLoaderData('main-summary');
  const { currentUser, setShowSideNav } = useContext(APP_CONTEXT);
  const { customerData, emailTemplateData, quoteResponseData } = routerData;
  const quoteType = () => {
    if (typeEnquire || !currentUser?.quoting_function_access) {
      return 'enquiry';
    }
    return 'quote';
  };

  const overrideUnrealImages = summary?.product?.override_unreal_images;

  const fetchUEImages = useCallback(() => {
    if (!overrideUnrealImages) {
      const {
        quote_options: quoteOptions,
        product,
        product_options: productOptions,
      } = quoteResponseData?.data ?? {};
      const combinedPartNumbers = [];
      quoteOptions.forEach((option) => {
        const { found, partNumber } = recursiveGetOptionName(
          option,
          productOptions,
          { name: '', partNumber: '' },
        );
        if (found) {
          if (!option.below_the_line) {
            combinedPartNumbers.push(partNumber);
          }
        }
      });
      // Remove duplicates by converting to a Set and back to an array
      const uniquePartNumbers = [...new Set(combinedPartNumbers)];
      if (uniquePartNumbers.length > 0) {
        ueImage(product?.id, uniquePartNumbers).then((res) => {
          setUeImages(res.data);
        });
      }
      if (uniquePartNumbers.length === 0) {
        ueImage(product?.id, []).then((res) => {
          setUeImages(res.data);
        });
      }
    }
  }, [overrideUnrealImages, quoteResponseData?.data, ueImage]);

  useEffect(() => {
    if (quoteManagement && initialLoad) {
      fetchUEImages();
      setInitialLoad(false);
    }
    setShowSideNav(false);
    return () => setShowSideNav(true);
  }, [fetchUEImages, initialLoad, quoteManagement, setShowSideNav]);

  const [updateQuote, { loading }] = useMutation(updateQuoteApi);
  const submitForm = async (values) => {
    setFormsLoading(true);

    let updatedQuote = {};

    const handleMediaUpload = async () => {
      const imageToTempS3 = async (image, path) => {
        if (image) {
          const { file, fileName, fileType, overrideImage } = image;
          if (overrideImage) {
            return image.file;
          }
          if (file && fileName && fileType) {
            try {
              const data = await uploadToTempS3Base64(
                file,
                `${path}/${fileName}`,
                fileType,
              );
              return data.id;
            } catch (error) {
              Alert({
                icon: 'error',
                message: `Failed to upload ${path} image.`,
              });
            }
          }
          return null;
        }
        return null;
      };

      const getImage = (index, image) => {
        if (image) {
          return image;
        }

        if (
          summary?.product?.override_unreal_images &&
          summary?.product_gallery?.length > index
        ) {
          return {
            file: summary?.product_gallery?.[index]?.image,
            overrideImage: true,
            fileName: 'img-1.jpg',
            fileType: 'image/jpeg',
          };
        }
        if (ueImages?.length > index && ueImages[index]?.image) {
          return {
            file: ueImages[index].image,
            fileName: 'img-1.jpg',
            fileType: 'image/jpeg',
          };
        }
        return null;
      };
      const firstImage = getImage(
        0,
        values.include_quote?.first_image ? null : values.quote.first_image,
      );
      const secondImage = getImage(
        1,
        values.include_quote?.second_image ? null : values.quote.second_image,
      );
      const thirdImage = getImage(
        2,
        values.include_quote?.third_image ? null : values.quote.third_image,
      );

      const promiseFirstImage = imageToTempS3(firstImage, 'quote-first-image');
      const promiseSecondImage = imageToTempS3(
        secondImage,
        'quote-second-image',
      );
      const promiseThirdImage = imageToTempS3(thirdImage, 'quote-third-image');
      const [firstImageResult, secondImageResult, thirdImageResult] =
        await Promise.all([
          promiseFirstImage,
          promiseSecondImage,
          promiseThirdImage,
        ]);
      updatedQuote.quote.first_image = firstImageResult;
      updatedQuote.quote.second_image = secondImageResult;
      updatedQuote.quote.third_image = thirdImageResult;
    };
    if (quoteType() === 'quote') {
      if (
        !values?.customer?.id ||
        !values?.contact_person?.id ||
        !values?.quote?.quantity
      ) {
        Alert({
          icon: 'error',
          message: 'Missing items in quote configuration',
        });
        setFormsLoading(false);
        navigate(`/shop/sales/main-summary/${quoteId}/quote`, { state });
        return;
      }

      const discountToNumber = !Number.isNaN(values?.quote?.discount)
        ? Number(values?.quote?.discount) / 100
        : 0;

      const combinedQuoteModules = [
        values?.quote_modules_prices,
        values?.quote_modules_details,
      ].flat();

      updatedQuote = {
        quote: {
          quote_number: values?.quote?.quote_number,
          customer_id: values?.customer?.id,
          contact_person_id: values?.contact_person?.id,
          discount: discountToNumber,
          override_unit_price: values?.quote?.override_unit_price,
          unit_price:
            values?.quote?.override_unit_price && values?.quote?.unit_price,
          quantity: Number(values?.quote?.quantity),
          discount_comment: values?.quote?.discount_comment,
          first_image: values?.quote?.first_image,
          second_image: values?.quote?.second_image,
          third_image: values?.quote?.third_image,
          cover_letter: values?.quote?.cover_letter,
        },
        quote_modules: {
          update: combinedQuoteModules.map((item) => ({
            id: item?.id,
            price: item?.price,
            value: item?.value,
          })),
        },
      };
    } else {
      if (!values?.customer?.id || !values?.contact_person?.id) {
        Alert({
          icon: 'error',
          message: 'Customer and Contact Person must be selected',
        });
        setFormsLoading(false);
        navigate(`/shop/sales/main-summary/${quoteId}/enquire`, { state });
        return;
      }
      updatedQuote = {
        quote: {
          customer_id: values?.customer?.id,
          contact_person_id: values?.contact_person?.id,
          comments: values?.quote?.comments,
        },
      };
    }

    await handleMediaUpload(updatedQuote);
    updateQuote(quoteId, updatedQuote)
      .then(() => {
        getQuotePdfApi(quoteId, quoteType())
          .then((response) => {
            const { data } = response;
            const { url } = data;
            // window.open(url, '_blank');
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `FileName.pdf`);

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode.removeChild(link);
            Alert({
              icon: 'success',
              message: 'Quote pdf generated successfully',
            });

            setFormsLoading(false);
          })
          .catch(() => {
            setFormsLoading(false);
            Alert({
              icon: 'error',
              message: 'Failed to get quote pdf',
            });
          });
      })
      .catch(() => {
        setFormsLoading(false);
        Alert({
          icon: 'error',
          message: 'Failed to update quote',
        });
      });
  };

  const imageArray = summary?.product_gallery?.map((item) => ({
    image: item?.image,
    description: item?.order,
  }));

  const tabData = [
    {
      label: 'Summary',
      path: `/shop/sales/main-summary/${quoteId}/summary`,
      useLocation: true,
      type: '*',
    },
    {
      label: 'Quote',
      path: `/shop/sales/main-summary/${quoteId}/quote`,
      useLocation: true,
      type: 'quote_access',
    },
    {
      label: 'Enquire',
      path: `/shop/sales/main-summary/${quoteId}/enquire`,
      useLocation: true,
      type: '*',
    },
  ];

  const summaryContext = {
    summary,
    setSummary,
    quoteCustomer,
    setQuoteCustomer,
    formsLoading,
    setFormsLoading,
    ...state,
  };

  if (loading || formsLoading) {
    return <Loader loading />;
  }
  return (
    <SummaryContextProvider value={summaryContext}>
      <TabHolder>
        <FormWrapper
          defaultValues={async () => {
            const quoteData = formData ?? quoteResponseData?.data;
            if (`${quoteData?.quote?.discount}`?.includes('.')) {
              quoteData.quote.discount *= 100;
              quoteData.quote.discount = Math.round(quoteData.quote.discount);
            } else {
              quoteData.quote.discount = quoteData?.quote?.discount ?? 0;
            }

            if (!quoteData?.quote?.quantity) {
              quoteData.quote.quantity = 1;
            }

            if (quoteData?.quote_modules_details?.length > 0) {
              quoteData.quote_modules_details =
                quoteData.quote_modules_details.map((detail) => {
                  const newDetail = detail;
                  if (newDetail?.value) {
                    const selectedOption = detail?.options?.find(
                      (option) => option?.value === detail?.value,
                    );
                    if (selectedOption) {
                      newDetail.selected = selectedOption?.value;
                    } else {
                      newDetail.selected = 'Other';
                    }
                  } else {
                    const defaultOption = detail?.options?.find(
                      (option) => option?.defualtOption === true,
                    );
                    if (defaultOption) {
                      newDetail.selected = defaultOption?.value;
                      newDetail.value = defaultOption?.value;
                    }
                  }
                  return newDetail;
                });
            }

            // if no previous cover letter, use saved email template as default
            const coverLetter =
              quoteData?.quote?.cover_letter !== null ||
              quoteData?.quote?.cover_letter === ''
                ? quoteData?.quote?.cover_letter
                : emailTemplateData?.data;

            quoteData.quote.cover_letter = coverLetter;

            setQuoteCustomer(customerData?.data);
            setSummary(quoteData);
            setFormsLoading(false);
            return { ...quoteData, customerData, emailTemplateData };
          }}
          onSubmit={submitForm}
          onEnterKeyPress={(e) => e.preventDefault()}
        >
          <TabSelector
            tabs={tabData}
            typeCheck={(type) => {
              if (type === 'quote_access') {
                return currentUser?.quoting_function_access;
              }
              return true;
            }}
          />
          {formsLoading ? (
            <Loader loading />
          ) : (
            <Row>
              <Col>
                <Card margin="25px 0 0 0">
                  <StyledColumn>
                    <Heading2Text>{summary?.product?.name}</Heading2Text>
                    {imageArray?.length || ueImages?.length ? (
                      <ImageCarousel
                        images={
                          ueImages?.length > 0 && !overrideUnrealImages
                            ? ueImages
                            : imageArray
                        }
                      />
                    ) : (
                      'No Images'
                    )}
                    <ButtonContainer>
                      {summary?.product_documents?.map((item) => (
                        <PrimaryOutlineButton
                          key={item.id}
                          title={item.name}
                          margin="0 0 10px 10px"
                          icon={<MdOutlineFileDownload />}
                          type="button"
                          onClick={() => window.open(item.document, '_blank')}
                        />
                      ))}
                    </ButtonContainer>
                  </StyledColumn>
                </Card>
              </Col>
              <Col>
                <OutletWrapper slide={25} slideUp={0} initial={false} />
              </Col>
            </Row>
          )}
          <BlackBottomBar
            buttonRightDisabled={formsLoading}
            buttonLeftTitle="Back"
            buttonRightTitle={`Generate ${quoteType().charAt(0).toUpperCase()}${quoteType().slice(1)} PDF`}
            buttonLeftOnClick={() => {
              if (!state || !state.selectedProduct || !state.selectedSupplier) {
                navigate('/shop/sales/configuration', {
                  state: {
                    quoteId,
                    selectedProduct: summary?.product,
                    selectedSupplier: {
                      id: summary?.product?.supplier_id,
                    },
                  },
                });
              } else {
                navigate('/shop/sales/configuration', {
                  state: {
                    ...state,
                    duplicate: false,
                  },
                });
              }
            }}
          />
        </FormWrapper>
      </TabHolder>
    </SummaryContextProvider>
  );
};

export default MainSummary;
